import React from 'react';
import { useRef, useEffect } from 'react';

import styled, { useTheme } from 'styled-components';

import { Translate } from 'react-localize-redux';

import { Option } from '@webapp/types/common/option';
import { DropdownContentType } from '@webapp/types/ui/dropdown-content-type';

import InputField from '@components/form/input';
import SearchIcon from '@components/basics/icons/search';

import DropdownHeader from '@components/form/dropdown/dropdown-header';
import DropdownList from '@components/form/dropdown/dropdown-list';
import DropdownItem from '@components/form/dropdown/dropdown-item';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import CircleRightArrowIcon from '@components/basics/icons/circle-right-arrow';
export interface DropdownCtrlProps<T> {
  id?: string;
  className?: any;
  headerClassName?: string;
  title?: React.ReactNode;
  options?: Option<T>[];
  children?: any;
  renderImage?: any;
  contentType?: DropdownContentType;
  value?: T;
  decoration?: React.ReactElement;
  smallDecoration?: boolean;
  isNameOnlyInput?: boolean;
  onChange?: (value: T) => void;
  onConfirmName?: () => void;
  valueMapper?: (value: T) => string;
  keyMapper?: (value: T) => string;
  header?: any;
  forceOpen?: boolean;
  disabled?: boolean;
  customBorderRadius?: string;
  hasFilter?: boolean;
  parentRelative?: boolean;
  customFilterValue?: string;
  onCustomFilter?: (filterValue: string) => void;
  filterLabel?: React.ReactNode;
  noMaxHeight?: boolean;
  maxHeight?: number;
  noScroll?: boolean;
  customContent?: boolean;
  noShadow?: boolean;
  isGrey?: boolean;
  isDark?: boolean;
  isDarker?: boolean;
  closeMenu?: boolean;
  onOpen?: (value: boolean) => void;
  onClose?: () => void;
  getNextPage?: (step: number) => void;
  redDot?: boolean;
  customListPadding?: string;
  customIcon?: React.ReactNode;
  hasPillShape?: boolean;
  hasChanges?: boolean;
  showError?: React.ReactNode;
  isCapitaliseOptions?: boolean;
  innerComponent?: React.ReactNode;
  close?: boolean;
  label?: any;
  type?: any;
  showSelectableOption?: boolean;
}

export const DropdownCtrl = <T,>(props: DropdownCtrlProps<T>) => {
  const theme = useTheme();
  const hasContent =
    props.options?.length || props.children || props.isNameOnlyInput;
  const {
    onOpen,
    getNextPage,
    children,
    onClose,
    close,
    label,
    type,
    closeMenu,
    isNameOnlyInput,
  } = props;

  const [listOpen, setListOpen] = React.useState(false);
  const [height, setHeight] = React.useState<number>();
  const [filterValue, setFilterValue] = React.useState<string>('');
  const [scroll, setScroll] = React.useState(0);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) {
        setListOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  React.useEffect(() => {
    if (onOpen) {
      onOpen(listOpen);
    } else if (onClose && !listOpen) {
      onClose();
    }
  }, [listOpen, onOpen, onClose]);

  React.useEffect(() => {
    if (closeMenu) {
      setListOpen(false);
    }
    if (isNameOnlyInput && label.length > 0) {
      setListOpen(true);
    }
    if (isNameOnlyInput && label.length === 0) {
      setListOpen(false);
    }
  }, [closeMenu, isNameOnlyInput, label]);

  React.useEffect(() => {
    if (close !== undefined) {
      setListOpen(!close);
    }
  }, [close]);

  React.useEffect(() => {
    if (getNextPage) {
      const timer = setTimeout(() => getNextPage(scroll > 0 ? 1 : 0), 200);

      return () => clearTimeout(timer);
    }
  }, [scroll, getNextPage]);

  React.useEffect(() => {
    setScroll(0);
  }, [children]);

  const renderValue = (value: T): string => {
    if (value !== undefined) {
      if (props.valueMapper) {
        return props.valueMapper(value);
      }

      return `${value}`;
    }

    return '';
  };

  const getOptions = () => {
    if (props.options) {
      const filterLabel = props.customFilterValue || filterValue;
      if (filterLabel) {
        return props.options?.filter((o) =>
          o.label.toLowerCase().includes(filterLabel.trim().toLowerCase())
        );
      }

      return props.options;
    }

    return [];
  };

  const getLabel = (): string => {
    if (props.value !== undefined) {
      if (props.options?.length) {
        const active = props.options.find((o) => {
          if (props.keyMapper) {
            return (
              props.keyMapper(o.value) === props.keyMapper(props.value as T)
            );
          }
          return o.value === props.value;
        });
        if (active) {
          return active.label;
        }
      } else {
        return renderValue(props.value);
      }
    }
    return '';
  };

  const getHeight = () => {
    if (height) {
      return height;
    }
    return undefined;
  };

  const toggleOpen = () => {
    if (props.disabled) {
      return;
    }

    if (hasContent) {
      return setListOpen(!listOpen);
    }

    return setListOpen(!!props.header);
  };

  const getKey = (value: T, index?: number) => {
    let key = renderValue(value).replace(/\s/g, '-').toLowerCase();

    if (props.keyMapper && value) {
      key = props.keyMapper(value);
    }

    return `dropdown-${key}-${index}`;
  };

  const handleClick = (value) => {
    if (props.onChange) {
      props.onChange(value);
    }

    setListOpen(false);
  };

  return (
    <div
      id={props.id}
      ref={ref}
      className={`${props.className} ${
        props.forceOpen || listOpen ? 'opened' : ''
      }`}
      onClick={props.header ? toggleOpen : undefined}
    >
      {props.header ? (
        props.header
      ) : (
        <DropdownHeader
          className={props.headerClassName}
          toggleOpen={toggleOpen}
          decoration={props.decoration}
          smallDecoration={props.smallDecoration}
          hasSelected={props.value !== undefined}
          title={props.title ? props.title : ''}
          label={getLabel()}
          renderImage={() => (props.renderImage ? props.renderImage() : null)}
          listOpen={listOpen}
          disabled={props.disabled}
          noShadow={props.noShadow}
          isGrey={props.isGrey}
          isDark={props.isDark}
          isDarker={props.isDarker}
          customBorderRadius={props.customBorderRadius}
          error={props.showError ? ' ' : undefined}
          redDot={props.redDot}
          customIcon={props.customIcon}
          hasPillShape={props.hasPillShape}
          hasChanges={props.hasChanges}
          capitaliseChildren={props.isCapitaliseOptions}
          showSelectableOption={props.showSelectableOption}
        />
      )}
      {hasContent ? (
        <DropdownList
          className="dropdown-list"
          listOpen={props.forceOpen || listOpen}
          onMouseOver={() => setListOpen(true)}
          content={props.contentType}
          height={getHeight()}
          customContent={props.customContent}
          noMaxHeight={props.noMaxHeight}
          maxHeight={props.maxHeight}
          noScroll={props.noScroll}
          customPadding={props.customListPadding}
          isDark={props.isDark || props.hasChanges}
          isDarker={props.isDarker || props.hasChanges}
          hasChanges={props.hasChanges}
        >
          {props.hasFilter ? (
            <form
              className="filter-wrapper"
              autoComplete="off"
              onClick={(ev) => ev.stopPropagation()}
              onSubmit={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();
                return false;
              }}
            >
              <InputField
                name="filter"
                label={props.filterLabel || ''}
                autoComplete="off"
                value={
                  props.customFilterValue
                    ? props.customFilterValue
                    : filterValue || ' '
                }
                tabIndex={-1}
                onChange={(value) => {
                  if (props.onCustomFilter) {
                    props.onCustomFilter(value.trim());
                  }
                  setFilterValue(value.trim());
                }}
                isGrey={props.hasPillShape}
                customBorderRadius="0"
                rightDecoration={<SearchIcon />}
              />
            </form>
          ) : props.innerComponent ? (
            <div className="inner-comp">{props.innerComponent}</div>
          ) : null}
          {props.options ? (
            <div
              className={`dropdown-list-content ${
                props.isDark || (props.hasPillShape && props.hasChanges)
                  ? 'dark'
                  : props.isDarker
                  ? 'darker'
                  : ''
              } ${props.hasPillShape ? 'pill-shape' : ''}`}
            >
              {type && (
                <>
                  <NewArtistOption
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={props.onConfirmName}
                  >
                    <Typography weight="700" color={theme.colors.blueColor}>
                      <Translate
                        id="RELEASE.INVITE.ARTIST"
                        data={{ name: label, type: type }}
                      />
                    </Typography>
                    <CircleRightArrowIcon />
                  </NewArtistOption>
                  <Separator />
                </>
              )}
              {getOptions().map((option, i) => (
                <DropdownItem
                  key={getKey(option.value, i)}
                  onClick={() => handleClick(option.value)}
                  decoration={option.decoration}
                  rightDecoration={option.rightDecoration}
                  helpText={option.helpText}
                  disabled={option.disabled}
                  value={option.value}
                  capitaliseChildren={props.isCapitaliseOptions}
                  isDark={
                    props.isDark ||
                    props.isDarker ||
                    props.isGrey ||
                    (props.hasPillShape && props.hasChanges)
                  }
                >
                  {option.label}
                </DropdownItem>
              ))}
            </div>
          ) : props.customContent ? (
            children
          ) : (
            <div
              className={`dropdown-list-content ${
                props.isDark || (props.hasPillShape && props.hasChanges)
                  ? 'dark'
                  : ''
              } ${props.hasPillShape ? 'pill-shape' : ''}`}
              ref={(el) => setHeight(el?.offsetHeight)}
              onScroll={() => (getNextPage ? setScroll(scroll + 1) : null)}
            >
              {children}
            </div>
          )}
        </DropdownList>
      ) : null}
    </div>
  );
};

export const GenericDropdownBuilder = <T,>() => styled(
  (props: DropdownCtrlProps<T>) => <DropdownCtrl<T> {...props} />
)`
  ${(props) => (props.parentRelative ? null : 'position: relative;')}

  &.opened {
    .dropdown-list {
      z-index: 999999;
    }
  }

  .filter-wrapper {
    position: relative;
    border-bottom: 3px solid ${({ theme }) => theme.colors.box};
  }
`;

const BasicDropdownCtrl = GenericDropdownBuilder<string>();

const NewArtistOption = styled(Flex)`
  cursor: pointer;
  padding: 10px;
  // background-color: ${({ theme }) => theme.colors.inputBackgroundAlternate};
  background-color: transparent;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.inputBackgroundAlternateDark};
  }
`;

const Separator = styled.div`
  height: 3px;
  background-color: ${({ theme }) => theme.colors.inputBackgroundAlternate};
`;

export default BasicDropdownCtrl;
